import React from 'react';
import {graphql} from 'gatsby';
import {ThemeProvider} from '@styles/themes';
import Layout from '@components/Layout';
import Meta from '@components/Meta';
import styled from 'styled-components';
import Fade from 'react-reveal/Fade';
import SubHeadline from '@components/Headline/SubHeadline';
import {Hero, HeroCaptionContainer} from '@components/Hero';
import PairBox from '@components/PairBox';
import IconCard from '@components/IconCard';
import {PagePadding} from '@components/SectionLayout';
import {media} from '@styles';
import {Headline, Paragraph, Icon, SolidButton} from '@components';
import * as colors from '@styles/colors';
import usda from '@images/icons/usda.png';
import gmo from '@images/icons/gmo.png';
import nohormones from '@images/icons/nohormones.png';
import SwiperCarousel from '@components/SwiperCarousel';

interface IngredientsProps {
  url: string;
  ourproductingredientdetailpage: {
    content: {
      bgimageBlock: any;
    }[]
  };
  title: string;
}

interface QueryDataProps {
  cms: {
    page: {
      seoTitle: string;
      searchDescription: string;
      content: any[]
    }
    exploreIngredients: IngredientsProps[];
  }
}
  
const HeadlineContainer = styled.div`
  margin-bottom: 25px;
  ${media.tablet`margin-bottom: 0;`}
`;

const SubHeadlineContainer = styled.div`
  margin-bottom: 35px;
  ${media.tablet`margin-bottom: 20px;`}
`;

const IconCardContainer = styled.div`
  margin: 0 1.8vw;

  ${media.tablet`margin: 0 5px`}
`;

const IconCardListContainer = styled.div`
  display: flex;
  justify-content: center;

  ${media.tablet`margin: 25px 0;`}
`;

const IconBoxContainer = styled.div<{width?: number}>`
  max-width: ${props => (props.width ? `${props.width}px` : 'auto')};
  margin: 100px auto;
  ${media.tablet`margin: 50px auto;`}
`;

const StyledIconCard = styled(IconCard)`
  h3 {
    ${media.tablet`display: none;`}
  }
`;

const InfoBoxParagraphContainer = styled.div`
  margin-bottom: 40px;

  ${media.tablet`margin-bottom: 30px;`}
`;

const PairBoxParagraphContainer = styled.div`
  margin-bottom: 30px;
`;

const InfoBoxButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const PairBoxOverRide = styled(PairBox)`
  ${media.tablet`
    .pairBoxImage {
      order: 1;
    }
    .pairBoxInfo {
      order: 2;
    }
  `}
`;

const HomePage = ({data} : {data: QueryDataProps}): JSX.Element => {
  const pageData = data.cms.page;
  const heroContent = pageData.content[0];
  const bodyContent = pageData.content[1];
  const discoverBlock = bodyContent.discoverBlock;
  const importBlock = bodyContent.importBlock;
  const learnmoreBlock = bodyContent.learnmoreBlock;
  const exploreIngredients = data.cms.exploreIngredients.map((ingr: IngredientsProps, index: number) => {
    let hero = ingr.ourproductingredientdetailpage.content[0];
    let ingrImg = hero ? hero.bgimageBlock : null;
    return {
      link: ingr.url,
      image: ingrImg,
      title: ingr.title,
    }
  });

  const heroLinks = [
    {
      title: heroContent.pagelink1Block.title,
      ariaLabel: heroContent.pagelink1Block.title,
      url: heroContent.pagelink1Block.url,
    },
    {
      title: heroContent.pagelink2Block.title,
      ariaLabel: heroContent.pagelink2Block.title,
      url: heroContent.pagelink2Block.url,
    },
  ];

  return (
    <ThemeProvider>
      <Layout>
        <Meta
          title={pageData.seoTitle || ''}
          description={pageData.searchDescription || ''}
          keywords={[
            `Organic Valley Ingredients`,
            `dairy`,
            `eggs`,
            `butter`,
            `meat`,
          ]}
        />
        {heroContent ? (
          <Hero height={682} gradient image={heroContent.bgimageBlock}>
            <HeroCaptionContainer links={heroLinks} width={680} yPosition="top">
              <HeadlineContainer>
                <Headline align={'center'}>{heroContent.headerBlock}</Headline>
              </HeadlineContainer>
              <SubHeadlineContainer>
                <SubHeadline>{heroContent.subheaderBlock}</SubHeadline>
              </SubHeadlineContainer>
            </HeroCaptionContainer>
          </Hero>
        ): null}

        <Fade>
          <PagePadding>
            <IconBoxContainer width={820}>
              <IconCardListContainer>
                <IconCardContainer>
                  <StyledIconCard title="USDA ORGANIC">
                    <Icon
                      alt="USDA ORGANIC"
                      src={usda}
                      width={90}
                      height={90}
                    />
                  </StyledIconCard>
                </IconCardContainer>
                <IconCardContainer>
                  <StyledIconCard title="NO GMOS">
                    <Icon alt="NO GMOS" src={gmo} width={113} height={90} />
                  </StyledIconCard>
                </IconCardContainer>
                <IconCardContainer>
                  <StyledIconCard title="NO HORMONES OR ANTIBIOTICS">
                    <Icon
                      alt="NO HORMONES OR ANTIBIOTICS"
                      src={nohormones}
                      width={105}
                      height={90}
                    />
                  </StyledIconCard>
                </IconCardContainer>
              </IconCardListContainer>
              
              {learnmoreBlock ? (
                <InfoBoxParagraphContainer>
                  <Paragraph align="center">
                    {learnmoreBlock.headerBlock}
                  </Paragraph>
                </InfoBoxParagraphContainer>
              ): null}
              {learnmoreBlock && learnmoreBlock.pagelinkBlock ? (
                <InfoBoxButtonContainer>
                  <SolidButton ariaLabel={learnmoreBlock.pagelinkBlock.title} url={learnmoreBlock.pagelinkBlock.url}>
                    Learn More
                </SolidButton>
                </InfoBoxButtonContainer>
              ) : null}
            </IconBoxContainer>
          </PagePadding>
        </Fade>

        {discoverBlock ? (
          <Fade>
            <PairBoxOverRide
              maxWidth={490}
              background={`${colors.creme}`}
              image={discoverBlock.imageBlock}
              title={discoverBlock.headerBlock}
            >
              {discoverBlock.bodyBlock ? (
                <PairBoxParagraphContainer>
                  <p>{discoverBlock.bodyBlock}</p>
                </PairBoxParagraphContainer>
              ) : null}
              {discoverBlock.pagelinkBlock ? (
                <SolidButton
                  ariaLabel={discoverBlock.pagelinkBlock.title}
                  url={discoverBlock.pagelinkBlock.url}
                >
                  {discoverBlock.pagelinkBlock.url === '/products/'
                    || discoverBlock.pagelinkBlock.title === 'Products'
                      ? "View Our Products" : discoverBlock.pagelinkBlock.title}
                </SolidButton>
              ) : null}
            </PairBoxOverRide>
          </Fade>
        ) : null}

        {importBlock ? (
          <Fade>
            <PairBoxOverRide
              maxWidth={520}
              reverse
              image={importBlock.imageBlock}
              title={importBlock.headerBlock}
            >
              {importBlock.bodyBlock ? (
                <PairBoxParagraphContainer>
                  <p>{importBlock.bodyBlock}</p>
                </PairBoxParagraphContainer>
              ) : null}
              {importBlock.pagelinkBlock ? (
                <SolidButton
                  ariaLabel={importBlock.pagelinkBlock.title}
                  url={importBlock.pagelinkBlock.url}
                >
                  {importBlock.pagelinkBlock.title}
                </SolidButton>
              ) : null}
            </PairBoxOverRide>
          </Fade>
        ) : null} 
        {exploreIngredients ? <SwiperCarousel data={exploreIngredients} /> : null}
      </Layout>
    </ThemeProvider>
  );
};

export default HomePage;

export const query = graphql`
  query HomePageQuery {
    cms {
      page(urlPath: "/home/") {
        ... on CMS_HomePage {
          seoTitle
          searchDescription
          content {
            ... on CMS_HomeHeroBlock {
              bgimageBlock {
                url
                childImageSharp {
                  fluid(maxWidth: 1440, quality: 100) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
              headerBlock
              subheaderBlock
              pagelink1Block {
                url
                title
              }
              pagelink2Block {
                url
                title
              }
            }
            ... on CMS_HomeBodyBlock {
              learnmoreBlock {
                headerBlock
                pagelinkBlock {
                  url
                  title
                }
              }
              discoverBlock {
                headerBlock
                bodyBlock
                pagelinkBlock {
                  url
                  title
                }
                imageBlock {
                  title
                  url
                  childImageSharp {
                    fluid(maxWidth: 800, quality: 100) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
              }
              importBlock {
                imageBlock {
                  title
                  url
                  childImageSharp {
                    fluid(maxWidth: 800, quality: 100) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
                headerBlock
                bodyBlock
                pagelinkBlock {
                  url
                  title
                }
              }
            }
          }
        }
      }
      exploreIngredients: pages(filter:{contentType:"ovingredientspages.ourproductingredientdetailpage"}) {
        title
        url
        ourproductingredientdetailpage {
          slug
          content {
            __typename
            ... on CMS_OurProductHeroBlock {
              headerBlock
              bgimageBlock {
                url
                childImageSharp {
                  fluid(maxWidth: 800, quality: 100) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;